import pt from '../../locales/pt';

/**
 * Etiqueta legível do estado do orçamento (evita «Aviso» genérico).
 * @param {string} status
 * @param {number|string|null|undefined} [spentPct]
 * @param {{ compact?: boolean }} [options]
 */
export function budgetStatusDisplayLabel(
  status,
  spentPct,
  { compact = false } = {},
) {
  const pct =
    spentPct != null && Number.isFinite(Number(spentPct))
      ? Math.round(Number(spentPct))
      : null;
  const labels = pt.budget.status;

  if (status === 'exceeded') {
    if (pct != null) {
      return compact ? `${pct}%` : `${labels.exceeded} (${pct}%)`;
    }
    return labels.exceeded;
  }
  if (status === 'reserved') {
    return labels.reserved;
  }
  if (status === 'no_budget') {
    return labels.no_budget;
  }
  return labels.ok;
}

/** Limite de grupo para vista de gastos (orçamentos por categoria, fallback alvo método). */
export function groupDisplayBudgetEur(group) {
  if (group?.is_pay_yourself_first) {
    if (group?.reserved_eur != null && group.reserved_eur !== '') {
      return Number(group.reserved_eur);
    }
    if (group?.target_budget_eur != null && group.target_budget_eur !== '') {
      return Number(group.target_budget_eur);
    }
  }
  if (group?.budget_eur != null && group.budget_eur !== '') {
    return Number(group.budget_eur);
  }
  if (group?.categories_budget_sum_eur != null && group.categories_budget_sum_eur !== '') {
    return Number(group.categories_budget_sum_eur);
  }
  if (group?.target_budget_eur != null && group.target_budget_eur !== '') {
    return Number(group.target_budget_eur);
  }
  return null;
}

/**
 * Valores do header do grupo em Despesas.
 * PYF: envelope reservado / alvo (sempre a fatia completa; barra a 100%).
 * Restantes: gasto / alocação do método (target_budget_eur = % rendimento),
 *   com fallback para a soma de orçamentos por categoria.
 */
export function groupExpensesHeaderAmounts(group) {
  if (group?.is_pay_yourself_first) {
    const reserved = Number(
      group.reserved_eur ?? group.target_budget_eur ?? group.budget_eur ?? 0,
    );
    const target = Number(
      group.target_budget_eur ?? group.reserved_eur ?? group.budget_eur ?? reserved,
    );
    const primary = Number.isFinite(reserved) ? reserved : 0;
    const secondary = Number.isFinite(target) && target > 0 ? target : primary;
    return {
      isReserved: true,
      primary,
      secondary,
    };
  }

  // preferir alocação do método; fallback para soma de orçamentos por categoria
  const methodTarget =
    group?.target_budget_eur != null && group.target_budget_eur !== ''
      ? Number(group.target_budget_eur)
      : null;
  const limit = methodTarget ?? groupDisplayBudgetEur(group);
  const spent = Number(group.spent_eur ?? 0);
  return {
    isReserved: false,
    primary: Number.isFinite(spent) ? spent : 0,
    secondary: limit,
  };
}

/** % do gasto do grupo face ao tecto do método (target_budget_eur). */
export function groupSpentVsTargetPct(group) {
  const target =
    group.target_budget_eur != null ? Number(group.target_budget_eur) : null;
  if (target == null || target <= 0) return null;
  const spent = Number(group.spent_eur ?? 0);
  return (spent / target) * 100;
}

/** % do gasto do grupo face à soma dos orçamentos por categoria. */
export function groupSpentVsBudgetPct(group) {
  const budget =
    group.budget_eur != null ? Number(group.budget_eur) : null;
  if (budget == null || budget <= 0) return null;
  const spent = Number(group.spent_eur ?? 0);
  return (spent / budget) * 100;
}

/** Estado visual do grupo vs disponível do método (não vs soma de orçamentos por categoria). */
export function groupSpentVsTargetStatus(group) {
  const pct = groupSpentVsTargetPct(group);
  if (pct == null) return group.status;
  return pct >= 100 ? 'exceeded' : 'ok';
}

/** Estado visual do grupo vs orçamento alocado (ciclos históricos). */
export function groupSpentVsBudgetStatus(group) {
  const pct = groupSpentVsBudgetPct(group);
  if (pct == null) return group.status;
  return pct >= 100 ? 'exceeded' : 'ok';
}

/** % da soma dos orçamentos por categoria face ao tecto do método. */
export function groupAllocatedVsTargetPct(group) {
  const target =
    group.target_budget_eur != null ? Number(group.target_budget_eur) : null;
  if (target == null || target <= 0) return null;
  const allocated = Number(group.categories_budget_sum_eur ?? 0);
  return (allocated / target) * 100;
}

/** Cores da barra de planeamento (alinhamento soma orçamentos vs método). */
export function groupAlignmentBarStatus(alignmentStatus) {
  if (alignmentStatus === 'over') return 'exceeded';
  return 'ok';
}

/** Cores da barra (gasto / disponível do grupo). */
export function allocationProgressStatus({ isReserved, spentVsTargetPct }) {
  if (isReserved) return 'allocated';

  const pct = spentVsTargetPct != null ? Number(spentVsTargetPct) : null;
  if (pct != null && pct >= 100) {
    return 'exceeded';
  }
  return 'ok';
}

export function cycleBudgetMonth(cycle) {
  if (!cycle?.end) {
    return { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };
  }
  const [year, month] = cycle.end.split('-').map(Number);
  return { year, month };
}
